import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../apiConfig';

export default function FarmProductsManager({ onSubmitting }) {
    const [centersData, setCentersData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCenterId, setSelectedCenterId] = useState('rajawaka');
    const [message, setMessage] = useState('');

    // Modal or input state for adding/editing categories & items
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');

    // Quick add item per category
    const [newItemTexts, setNewItemTexts] = useState({});

    // Center name editing
    const [editingCenterName, setEditingCenterName] = useState(false);
    const [centerNameInput, setCenterNameInput] = useState('');

    // Fetch initial farm products data
    const fetchFarmProducts = async () => {
        setLoading(true);
        try {
            const url = getApiUrl(`api_farm_products.php?t=${Date.now()}`);
            const res = await fetch(url);
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data) && data.length > 0) {
                    setCentersData(data);
                    try { localStorage.setItem('site_farm_products', JSON.stringify(data)); } catch (e) {}
                    return;
                }
            }
            throw new Error('API fetch failed');
        } catch (err) {
            console.warn('Farm products API failed, trying fallback...', err);
            try {
                const fbRes = await fetch(`/farm_products_data.json?t=${Date.now()}`);
                if (fbRes.ok) {
                    const fbData = await fbRes.json();
                    if (Array.isArray(fbData) && fbData.length > 0) {
                        setCentersData(fbData);
                        return;
                    }
                }
            } catch (fbErr) {
                console.warn('Static json fallback failed:', fbErr);
            }

            const local = localStorage.getItem('site_farm_products');
            if (local) {
                try {
                    setCentersData(JSON.parse(local));
                } catch (pe) {
                    setCentersData([]);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFarmProducts();
    }, []);

    // Get currently selected center
    const currentCenter = centersData.find(c => c.id === selectedCenterId) || centersData[0];

    // Helper: Save whole dataset to server & local storage
    const saveData = async (updatedData, successMsg = '✅ සාර්ථකව සුරැකිණි!') => {
        setCentersData(updatedData);
        if (onSubmitting) onSubmitting(true);
        setMessage('');

        try {
            localStorage.setItem('site_farm_products', JSON.stringify(updatedData));
        } catch (e) {}

        try {
            const res = await fetch(getApiUrl('api_farm_products.php'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedData)
            });

            if (res.ok) {
                setMessage(successMsg);
            } else {
                throw new Error('Server returned non-ok status');
            }
        } catch (err) {
            console.error('Server sync failed:', err);
            setMessage('⚠ විස්තර Local Storage හි සුරැකිණි. සර්වර් වෙත sync වීම ප්‍රමාද විය හැක.');
        } finally {
            if (onSubmitting) onSubmitting(false);
            setTimeout(() => setMessage(''), 3500);
        }
    };

    // 1. ADD NEW CATEGORY to Current Center
    const handleAddCategory = (e) => {
        e.preventDefault();
        if (!newCategoryName.trim() || !currentCenter) return;

        const newCat = {
            id: Date.now(),
            label: newCategoryName.trim(),
            options: []
        };

        const updatedCenters = centersData.map(c => {
            if (c.id === currentCenter.id) {
                return {
                    ...c,
                    categories: [...(c.categories || []), newCat]
                };
            }
            return c;
        });

        saveData(updatedCenters, `✅ "${newCat.label}" කාණ්ඩය සාර්ථකව එක් කරන ලදී!`);
        setNewCategoryName('');
    };

    // 2. UPDATE CATEGORY NAME
    const handleSaveCategoryName = (catId) => {
        if (!editCategoryName.trim()) return;

        const updatedCenters = centersData.map(c => {
            if (c.id === currentCenter.id) {
                return {
                    ...c,
                    categories: (c.categories || []).map(cat => 
                        cat.id === catId ? { ...cat, label: editCategoryName.trim() } : cat
                    )
                };
            }
            return c;
        });

        saveData(updatedCenters, '✅ කාණ්ඩයේ නම යාවත්කාලීන විය!');
        setEditingCategoryId(null);
        setEditCategoryName('');
    };

    // 3. DELETE CATEGORY
    const handleDeleteCategory = (catId, catLabel) => {
        if (!window.confirm(`ඔබට "${catLabel}" කාණ්ඩය සහ එහි ඇති සියලුම නිෂ්පාදන ඉවත් කිරීමට අවශ්‍ය ද?`)) return;

        const updatedCenters = centersData.map(c => {
            if (c.id === currentCenter.id) {
                return {
                    ...c,
                    categories: (c.categories || []).filter(cat => cat.id !== catId)
                };
            }
            return c;
        });

        saveData(updatedCenters, `🗑️ "${catLabel}" කාණ්ඩය ඉවත් කරන ලදී.`);
    };

    // 4. ADD ITEM TO CATEGORY
    const handleAddItem = (catId) => {
        const itemText = (newItemTexts[catId] || '').trim();
        if (!itemText) return;

        const newItem = {
            id: Date.now(),
            label: itemText
        };

        const updatedCenters = centersData.map(c => {
            if (c.id === currentCenter.id) {
                return {
                    ...c,
                    categories: (c.categories || []).map(cat => {
                        if (cat.id === catId) {
                            return {
                                ...cat,
                                options: [...(cat.options || []), newItem]
                            };
                        }
                        return cat;
                    })
                };
            }
            return c;
        });

        saveData(updatedCenters, `✅ "${itemText}" නිෂ්පාදනය එක් කරන ලදී!`);
        setNewItemTexts(prev => ({ ...prev, [catId]: '' }));
    };

    // 5. DELETE ITEM FROM CATEGORY
    const handleDeleteItem = (catId, itemId, itemLabel) => {
        if (!window.confirm(`"${itemLabel}" ඉවත් කරන්න ද?`)) return;

        const updatedCenters = centersData.map(c => {
            if (c.id === currentCenter.id) {
                return {
                    ...c,
                    categories: (c.categories || []).map(cat => {
                        if (cat.id === catId) {
                            return {
                                ...cat,
                                options: (cat.options || []).filter(opt => opt.id !== itemId)
                            };
                        }
                        return cat;
                    })
                };
            }
            return c;
        });

        saveData(updatedCenters, `🗑️ "${itemLabel}" ඉවත් කරන ලදී.`);
    };

    // 6. EDIT ITEM IN CATEGORY
    const handleEditItem = (catId, itemId, currentLabel) => {
        const newName = window.prompt('නිෂ්පාදනයේ නව නම ඇතුලත් කරන්න:', currentLabel);
        if (newName === null || !newName.trim()) return;

        const updatedCenters = centersData.map(c => {
            if (c.id === currentCenter.id) {
                return {
                    ...c,
                    categories: (c.categories || []).map(cat => {
                        if (cat.id === catId) {
                            return {
                                ...cat,
                                options: (cat.options || []).map(opt => 
                                    opt.id === itemId ? { ...opt, label: newName.trim() } : opt
                                )
                            };
                        }
                        return cat;
                    })
                };
            }
            return c;
        });

        saveData(updatedCenters, '✅ නිෂ්පාදනයේ නම යාවත්කාලීන විය!');
    };

    // 7. EDIT CENTER NAME
    const handleSaveCenterName = () => {
        if (!centerNameInput.trim()) return;

        const updatedCenters = centersData.map(c => {
            if (c.id === currentCenter.id) {
                return { ...c, name: centerNameInput.trim() };
            }
            return c;
        });

        saveData(updatedCenters, '✅ මධ්‍යස්ථානයේ නම යාවත්කාලීන විය!');
        setEditingCenterName(false);
    };

    return (
        <div className="farm-products-manager-container" style={{ padding: '10px 0' }}>
            {/* Header info banner */}
            <div style={{
                background: 'linear-gradient(135deg, #0e633d 0%, #168052 100%)',
                color: 'white',
                padding: '24px 28px',
                borderRadius: '14px',
                marginBottom: '24px',
                boxShadow: '0 4px 18px rgba(14, 99, 61, 0.15)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                <div>
                    <h2 style={{ margin: '0 0 6px 0', fontSize: '24px', fontWeight: '700', letterSpacing: '-0.3px' }}>
                        🌾 ගොවිපළ නිෂ්පාදන කළමනාකරණය (Farm Products)
                    </h2>
                    <p style={{ margin: 0, opacity: 0.92, fontSize: '14px' }}>
                        ගොවිපළ හා අභ්‍යාස ආයතන වල ඇති පැළ, බීජ හා අස්වනු නිෂ්පාදන කාණ්ඩ (Categories) සහ වර්ග (Items) ඇතුලත් කර කළමනාකරණය කරන්න.
                    </p>
                </div>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.18)',
                    backdropFilter: 'blur(8px)',
                    padding: '10px 18px',
                    borderRadius: '10px',
                    textAlign: 'center'
                }}>
                    <span style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>මධ්‍යස්ථාන ගණන</span>
                    <strong style={{ fontSize: '22px' }}>{centersData.length}</strong>
                </div>
            </div>

            {/* Notification alert */}
            {message && (
                <div style={{
                    padding: '14px 20px',
                    borderRadius: '10px',
                    marginBottom: '20px',
                    fontWeight: '600',
                    fontSize: '15px',
                    backgroundColor: message.includes('⚠') ? '#fff3cd' : '#d1e7dd',
                    color: message.includes('⚠') ? '#856404' : '#0f5132',
                    border: message.includes('⚠') ? '1px solid #ffeeba' : '1px solid #badbcc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                    <span>{message}</span>
                    <button 
                        onClick={() => setMessage('')} 
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Step 1: Select Farm Center */}
            <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                padding: '24px 28px',
                marginBottom: '26px',
                boxShadow: '0 3px 14px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e2e8f0'
            }}>
                <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '10px', fontSize: '15px' }}>
                    🏢 ගොවිපළ / අභ්‍යාස මධ්‍යස්ථානය තෝරන්න (Select Farm / Training Center):
                </label>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <select
                        value={selectedCenterId}
                        onChange={(e) => {
                            setSelectedCenterId(e.target.value);
                            setEditingCenterName(false);
                        }}
                        style={{
                            flex: '1',
                            minWidth: '280px',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '1.5px solid #0e633d',
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#0e633d',
                            outline: 'none',
                            background: '#f0fdf4'
                        }}
                    >
                        {centersData.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        onClick={() => {
                            setCenterNameInput(currentCenter?.name || '');
                            setEditingCenterName(!editingCenterName);
                        }}
                        style={{
                            background: '#f8fafc',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            padding: '11px 18px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        ✏️ නම සංස්කරණය
                    </button>

                    <button
                        type="button"
                        onClick={fetchFarmProducts}
                        title="දත්ත Refresh කරන්න"
                        style={{
                            background: '#f8fafc',
                            border: '1px solid #cbd5e1',
                            padding: '11px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer'
                        }}
                    >
                        🔄
                    </button>
                </div>

                {/* Edit Center Name field */}
                {editingCenterName && (
                    <div style={{ marginTop: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                            type="text"
                            value={centerNameInput}
                            onChange={(e) => setCenterNameInput(e.target.value)}
                            style={{
                                flex: '1',
                                padding: '10px 14px',
                                borderRadius: '6px',
                                border: '1.5px solid #cbd5e1',
                                fontSize: '14.5px'
                            }}
                        />
                        <button
                            type="button"
                            onClick={handleSaveCenterName}
                            style={{
                                background: '#0e633d',
                                color: 'white',
                                border: 'none',
                                padding: '10px 18px',
                                borderRadius: '6px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            සුරකින්න
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditingCenterName(false)}
                            style={{
                                background: '#f1f5f9',
                                color: '#475569',
                                border: '1px solid #cbd5e1',
                                padding: '10px 14px',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            අවලංගු
                        </button>
                    </div>
                )}
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: '15px', color: '#0e633d', fontWeight: '600', marginBottom: '15px' }}>
                    🔄 දත්ත ලබාගනිමින් පවතී (Loading farm products)...
                </div>
            )}

            {/* Step 2: Manage Categories and Items for Selected Center */}
            {currentCenter && (
                <div>
                    {/* Add New Category Card */}
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '14px',
                        padding: '22px 28px',
                        marginBottom: '26px',
                        boxShadow: '0 3px 14px rgba(0, 0, 0, 0.06)',
                        border: '1px dashed #0e633d'
                    }}>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#0e633d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>➕ නව නිෂ්පාදන කාණ්ඩයක් එක් කරන්න (Add New Category to this Center):</span>
                        </h4>
                        <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                placeholder="උදා: බද්ධ පැළ / බීජ පැළ / අස්වනු නිෂ්පාදන / මී පැණි"
                                style={{
                                    flex: '1',
                                    minWidth: '280px',
                                    padding: '11px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    fontSize: '14.5px',
                                    outline: 'none'
                                }}
                            />
                            <button
                                type="submit"
                                style={{
                                    background: '#0e633d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '11px 24px',
                                    borderRadius: '8px',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(14, 99, 61, 0.2)'
                                }}
                            >
                                ➕ කාණ්ඩය එක් කරන්න
                            </button>
                        </form>
                    </div>

                    {/* Categories List */}
                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>
                            📦 {currentCenter.name} හි නිෂ්පාදන කාණ්ඩ ({currentCenter.categories?.length || 0})
                        </h3>
                    </div>

                    {(!currentCenter.categories || currentCenter.categories.length === 0) ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            background: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            color: '#64748b'
                        }}>
                            මෙම මධ්‍යස්ථානය සඳහා තවමත් නිෂ්පාදන කාණ්ඩ ඇතුලත් කර නොමැත. ඉහත පෝරමයෙන් නව කාණ්ඩයක් එක් කරන්න.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '22px' }}>
                            {currentCenter.categories.map((cat) => (
                                <div
                                    key={cat.id}
                                    style={{
                                        background: '#ffffff',
                                        borderRadius: '14px',
                                        border: '1.5px solid #e2e8f0',
                                        boxShadow: '0 3px 12px rgba(0, 0, 0, 0.05)',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    {/* Category Card Header */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #0e633d 0%, #168052 100%)',
                                        color: 'white',
                                        padding: '16px 20px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        {editingCategoryId === cat.id ? (
                                            <div style={{ display: 'flex', gap: '6px', flex: '1', marginRight: '10px' }}>
                                                <input
                                                    type="text"
                                                    value={editCategoryName}
                                                    onChange={(e) => setEditCategoryName(e.target.value)}
                                                    style={{
                                                        flex: '1',
                                                        padding: '6px 10px',
                                                        borderRadius: '4px',
                                                        border: 'none',
                                                        fontSize: '14px',
                                                        color: '#111'
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleSaveCategoryName(cat.id)}
                                                    style={{ background: '#ffffff', color: '#0e633d', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', fontWeight: 'bold' }}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingCategoryId(null)}
                                                    style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer' }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700', letterSpacing: '-0.2px' }}>
                                                {cat.label}
                                            </h4>
                                        )}

                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingCategoryId(cat.id);
                                                    setEditCategoryName(cat.label);
                                                }}
                                                style={{
                                                    background: 'rgba(255, 255, 255, 0.2)',
                                                    border: 'none',
                                                    color: 'white',
                                                    padding: '5px 8px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                                title="කාණ්ඩයේ නම වෙනස් කරන්න"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteCategory(cat.id, cat.label)}
                                                style={{
                                                    background: 'rgba(239, 68, 68, 0.85)',
                                                    border: 'none',
                                                    color: 'white',
                                                    padding: '5px 8px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                                title="කාණ්ඩය ඉවත් කරන්න"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>

                                    {/* Items List */}
                                    <div style={{ padding: '16px 20px', flex: '1', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontSize: '12.5px', color: '#64748b', marginBottom: '10px', fontWeight: '600' }}>
                                            ඇතුලත් නිෂ්පාදන වර්ග ({cat.options?.length || 0}):
                                        </div>

                                        <div style={{ flex: '1', marginBottom: '14px' }}>
                                            {(!cat.options || cat.options.length === 0) ? (
                                                <div style={{ fontStyle: 'italic', color: '#94a3b8', fontSize: '13px', padding: '10px 0' }}>
                                                    තවමත් මෙම කාණ්ඩයට නිෂ්පාදන එක් කර නැත.
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {cat.options.map(opt => (
                                                        <div
                                                            key={opt.id}
                                                            style={{
                                                                background: '#f8fafc',
                                                                border: '1px solid #e2e8f0',
                                                                borderRadius: '8px',
                                                                padding: '8px 12px',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                fontSize: '14.5px',
                                                                transition: 'background-color 0.15s'
                                                            }}
                                                        >
                                                            <span style={{ fontWeight: '500', color: '#1e293b' }}>
                                                                • {opt.label}
                                                            </span>
                                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleEditItem(cat.id, opt.id, opt.label)}
                                                                    style={{
                                                                        background: '#e0f2fe',
                                                                        color: '#0284c7',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        padding: '3px 7px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '11px',
                                                                        fontWeight: '600'
                                                                    }}
                                                                    title="සංස්කරණය"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteItem(cat.id, opt.id, opt.label)}
                                                                    style={{
                                                                        background: '#fee2e2',
                                                                        color: '#ef4444',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        padding: '3px 7px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '11px',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                    title="ඉවත් කරන්න"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Quick Add Sub-Item */}
                                        <div style={{ marginTop: 'auto', paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <input
                                                    type="text"
                                                    value={newItemTexts[cat.id] || ''}
                                                    onChange={(e) => setNewItemTexts({ ...newItemTexts, [cat.id]: e.target.value })}
                                                    placeholder="නව වර්ගයක් (උදා: ගම්මිරිස් පැළ)"
                                                    style={{
                                                        flex: '1',
                                                        padding: '8px 10px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #cbd5e1',
                                                        fontSize: '13px',
                                                        outline: 'none'
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddItem(cat.id);
                                                        }
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddItem(cat.id)}
                                                    style={{
                                                        background: '#0e633d',
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '8px 14px',
                                                        borderRadius: '6px',
                                                        fontSize: '13px',
                                                        fontWeight: '700',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    + එක් කරන්න
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
